/**
 * The client half of `POST /api/btp/agreements/import` — Supplier Relations'
 * Smartsheet "All Stages" export, previewed and then committed.
 *
 * The importer has existed and worked since #1839. What did not exist was any
 * way to run it that was not an authenticated curl with a bearer token, which
 * is why Stacie's top ask — "put the actual Smartsheet agreements in there…
 * that's going to give me so much more of a clear picture" — has been blocked on
 * nothing but a UI.
 *
 * ── Three things here are load-bearing, and each of them is a failure that
 *    would be INVISIBLE afterwards ─────────────────────────────────────────────
 *
 * 1. THE TWO RESPONSES HAVE DIFFERENT COUNT KEYS. A dry run answers
 *    `counts: {create, update, reject, skip}` (the plan's `RowOutcome`s) and a
 *    commit answers `counts: {created, updated, rejected, skipped}` (the apply
 *    result's, past tense). One reader for both renders `undefined` — which
 *    formats as "0 created" and reads as a no-op import. `normaliseCounts`
 *    exists so there is exactly one place that knows this.
 *
 * 2. `commit=true` IS THE ONLY DIFFERENCE BETWEEN A PREVIEW AND A WRITE, and
 *    the route's default is deliberately the safe one. So a dropped or
 *    misspelled param does not fail — it silently returns a dry run, and the
 *    caller who believes they imported 2,670 agreements has imported none.
 *    `assertCommitted` refuses to let that read as success: the response's own
 *    `committed` flag is the authority, never the request we think we sent.
 *
 * 3. THE BYTES PREVIEWED MUST BE THE BYTES COMMITTED. The route recomputes the
 *    plan on the commit call — it takes no `run_id` — so preview and commit are
 *    two independent reads of whatever it is handed. A `File` handle reads from
 *    disk lazily, so re-reading it at commit time can pick up a file the user
 *    edited in between and write a plan nobody reviewed. Both functions take an
 *    `ArrayBuffer` the caller read ONCE, and cannot re-read the file at all.
 */
/**
 * The `apiFetch` seam, passed in rather than imported, so every test here runs
 * without MSAL. Same shape `extractFromDocument` takes, for the same reason.
 */
export type ApiFetch = <T>(url: string, init?: RequestInit) => Promise<T>;

/** Mirrors the API's own ceiling, so an oversized file is refused before the
 *  bytes cross the wire rather than after. */
export const MAX_IMPORT_BYTES = 25 * 1024 * 1024;

/** How the matcher found the existing agreement a row would write to. */
export type MatchStrength = 'row_id' | 'negotiation_id' | 'composite' | 'none';

/** Plan-time outcomes (present tense — nothing has happened yet). */
export type PlannedOutcome = 'create' | 'update' | 'reject' | 'skip';

/** Write-time outcomes (past tense). No `skipped`: a skipped row is never
 *  attempted, so the apply result does not carry one per row. */
export type AppliedOutcome = 'created' | 'updated' | 'rejected';

export interface PreviewRow {
  line: number;
  outcome: PlannedOutcome;
  match: MatchStrength;
  reasons: string[];
}

export interface AppliedRow {
  line: number;
  outcome: AppliedOutcome;
  agreementId?: string;
  reasons: string[];
}

export interface PreviewResponse {
  committed: false;
  run_id: string;
  rows_in_file: number;
  counts: Record<PlannedOutcome, number>;
  unknown_statuses: string[];
  unmapped_headers: string[];
  warnings: string[];
  rows: PreviewRow[];
  rows_truncated: number;
}

export interface CommitResponse {
  committed: boolean;
  run_id: string;
  rows_in_file: number;
  counts: {
    created: number;
    updated: number;
    rejected: number;
    skipped: number;
  };
  properties_created: number;
  contacts_seeded: number;
  rate_tests_seeded: number;
  unknown_statuses: string[];
  /** NOT returned by the commit call — the preview's list is the one to show. */
  unmapped_headers?: string[];
  warnings: string[];
  rows: AppliedRow[];
  rows_truncated: number;
}

/** The four numbers, in the one vocabulary the screen speaks. */
export interface Counts {
  create: number;
  update: number;
  reject: number;
  skip: number;
}

/**
 * Read both response shapes into one. See note 1 above: this is the only place
 * that knows the commit response speaks in the past tense.
 */
export function normaliseCounts(res: PreviewResponse | CommitResponse): Counts {
  if (res.committed) {
    const c = (res as CommitResponse).counts;
    return {
      create: c.created ?? 0,
      update: c.updated ?? 0,
      reject: c.rejected ?? 0,
      skip: c.skipped ?? 0,
    };
  }
  const c = (res as PreviewResponse).counts;
  return {
    create: c.create ?? 0,
    update: c.update ?? 0,
    reject: c.reject ?? 0,
    skip: c.skip ?? 0,
  };
}

/**
 * Non-ASCII cannot be transmitted in a header value, and the server uses this
 * only for the run record's filename — so degrading the name beats failing the
 * upload of a sheet exported as "All Stages – Sept.csv" (that dash is U+2013).
 */
export function headerSafeFilename(name: string): string {
  // eslint-disable-next-line no-control-regex
  return name.replace(/[^\x20-\x7E]/g, '_');
}

function importInit(bytes: ArrayBuffer, filename: string): RequestInit {
  return {
    method: 'POST',
    body: bytes,
    headers: {
      'x-upload-filename': headerSafeFilename(filename),
      'Content-Type': 'text/csv',
    },
  };
}

/**
 * The dry run. No `commit` param at all rather than `commit=false`: the route
 * tests for the exact string `'true'`, and sending the negative spelling invites
 * someone to "fix" it later by flipping the value.
 */
export function previewImport(
  bytes: ArrayBuffer,
  filename: string,
  apiFetch: ApiFetch,
): Promise<PreviewResponse> {
  return apiFetch<PreviewResponse>(
    '/api/btp/agreements/import',
    importInit(bytes, filename),
  );
}

/** The write. One transaction server-side: it lands whole or not at all. */
export function commitImport(
  bytes: ArrayBuffer,
  filename: string,
  apiFetch: ApiFetch,
): Promise<CommitResponse> {
  return apiFetch<CommitResponse>(
    '/api/btp/agreements/import?commit=true',
    importInit(bytes, filename),
  );
}

/**
 * Thrown when a commit call comes back as a dry run. See note 2: this is the
 * one failure mode where doing nothing looks exactly like doing everything.
 */
export class NotCommittedError extends Error {
  constructor() {
    super(
      'The server answered with a PREVIEW, not an import — nothing was written. ' +
        'This means the commit instruction did not reach it. Do not re-run until ' +
        'someone has checked why, and report it with the run id.',
    );
    this.name = 'NotCommittedError';
  }
}

export function assertCommitted(res: CommitResponse): CommitResponse {
  if (res.committed !== true) throw new NotCommittedError();
  return res;
}

/**
 * What the commit button promises, built from the plan rather than from prose.
 *
 * The numbers ARE the confirmation step. A button reading "Import" asks the
 * reader to remember what they just scrolled past; one reading "Create 1,204
 * and update 812 agreements" cannot be pressed by accident on the wrong file.
 */
export function describeCommit(counts: Counts): string {
  const parts: string[] = [];
  if (counts.create > 0) parts.push(`create ${fmt(counts.create)}`);
  if (counts.update > 0) parts.push(`update ${fmt(counts.update)}`);
  if (parts.length === 0) return 'Nothing to import';
  return `Import — ${parts.join(' and ')} ${
    counts.create + counts.update === 1 ? 'agreement' : 'agreements'
  }`;
}

/** True when the plan would write nothing. The button is pointless, and worse,
 *  pressing it would open a two-minute transaction to no purpose. */
export function writesNothing(counts: Counts): boolean {
  return counts.create + counts.update === 0;
}

export function fmt(n: number): string {
  return n.toLocaleString('en-US');
}

/** Reader-facing label per outcome. `skip` needs the WHY in the word: the
 *  reasons column carries it per row, but the summary tile has to stand alone. */
export const PLANNED_LABELS: Record<PlannedOutcome, string> = {
  create: 'New agreements',
  update: 'Existing agreements updated',
  reject: 'Cannot be imported',
  skip: 'Left alone',
};

export const APPLIED_LABELS: Record<PlannedOutcome, string> = {
  create: 'Created',
  update: 'Updated',
  reject: 'Rejected',
  skip: 'Skipped',
};

/**
 * Which row group the report should open on.
 *
 * Rejects are the only group anybody can act on — every one is an
 * identification problem, not a data problem — so the list opens there when
 * there are any to look at.
 *
 * KEYED OFF THE ROWS, NOT THE COUNTS, and that distinction is the whole reason
 * this is a function. The API truncates its row list at 200 while the counts
 * describe all 2,670, so "9 rejected" can perfectly well come back with no
 * rejected row in the listed set — and filtering to rejects then opens an empty
 * table directly beneath a report saying nine rows need attention.
 */
export function openingFilter(
  rows: readonly { outcome: string }[],
  rejectOutcome: 'reject' | 'rejected',
): 'reject' | 'all' {
  return rows.some((r) => r.outcome === rejectOutcome) ? 'reject' : 'all';
}
